import { ReadSearchApiArg, SearchScope } from 'lib-search-types';
import { useEffect, useRef } from 'react';

import {
  ReadSearchInfiniteQueryOptions,
  useLazyReadSearchInfiniteQuery,
} from '../useLazyReadSearchInfiniteQuery';

import {
  _InferEnriched,
  _InferScope,
  UseReadSearchInfiniteQueryResult,
} from './useReadSearchInfiniteQuery.types';

/**
 * A hook to call the Search API with infinite scrolling/pagination support,
 * modelled after RTK Query's `useInfiniteQuery`.
 *
 * This is the eager variant, which automatically triggers a search on mount,
 * and whenever the provided `arg` changes.
 *
 * @example
 *   ```tsx
 *   const { results, isFetching, hasNextPage, fetchNextPage } = useReadSearchInfiniteQuery({
 *     scope: 'users',
 *     sortBy: { field: 'display_name', order: 'asc' },
 *   });
 *   ```;
 *
 * @see {@link useLazyReadSearchInfiniteQuery} for the lazy variant.
 */
export const useReadSearchInfiniteQuery = <
  TArg extends ReadSearchApiArg<SearchScope, boolean>,
>(
  arg: TArg,
  options?: ReadSearchInfiniteQueryOptions,
): UseReadSearchInfiniteQueryResult<
  _InferScope<TArg>,
  _InferEnriched<TArg>
> => {
  const [trigger, result] = useLazyReadSearchInfiniteQuery<
    _InferScope<TArg>,
    _InferEnriched<TArg>
  >(options);

  // Keep trigger in a ref so the effect never re-fires due to identity changes.
  const triggerRef = useRef(trigger);
  triggerRef.current = trigger;

  // Serialize the arg for a stable primitive dependency — avoids infinite loops
  // caused by inline object literals getting new references on every render.
  const argKey = JSON.stringify(arg);

  useEffect(() => {
    triggerRef.current(
      arg as unknown as ReadSearchApiArg<
        _InferScope<TArg>,
        _InferEnriched<TArg>
      >,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [argKey]);

  return result;
};
